import { type ChangeEvent, type FC, memo, useEffect, useMemo, useRef } from "react";
import DateInput from "~/common/components/DateInput";
import classNames from "classnames";
import CartQuantityInput from "~/features/cart/activeQuote/components/productTable/CartQuantityInput";
import { type PreviewItem, type PreviewItemUpdateData } from "~/features/bulkAddToCart/types/models";
import PreviewTableMaterialIdInput from "~/features/bulkAddToCart/components/PreviewTable/PreviewTableMaterialIdInput";
import PriceReferenceDropdown from "~/features/productDetails/components/AddToCartCard/PriceReferenceDropdown";
import Parameter from "~/common/components/Parameter";
import { canUseCommentsForProduct } from "~/utils/productUtils";
import { type ProductData, ProductMarketType } from "~/domains/product/types/ProductData";
import { Card } from "~/features/configurator/Linear/components/Card";
import { isDateInvalidForDelivery } from "~/common/utils/dateUtils";

import { isLineLevelCustomerOrderNumberEnabled } from "~/utils/features";
import { Trans, useTranslation } from "react-i18next";
import { InfoProductMessagePMA } from "~/features/productDetails/components/ProductSummary/InfoProductMessagePMA";
import { Stack, styled, TextField, Typography } from "~/common/mui";
import { Link } from "~/common/components/Link";
import classes from "~/features/bulkAddToCart/components/PreviewTable/PreviewTableRow.module.less";
import CommentsDisabledOutlinedIcon from "@mui/icons-material/CommentsDisabledOutlined";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { C_ERROR, C_INFORMATION } from "~/common/components/icon/Colors";
import { Alert } from "~/common/mui/Alert/Alert";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";

interface Props {
  index: number;
  data: PreviewItem;
  onDataChange: (id: number, newData: Partial<PreviewItemUpdateData>) => void;
  onRemoveItem: (id: number) => void;
  onSetCommentVisibility: (id: number, visible: boolean) => void;
  showPriceReference: boolean;
  onRender?: (element: Element) => void;
}

const PreviewTableRowWrapper: FC<Props & JSX.IntrinsicElements["div"]> = (props) => {
  const {
    index,
    data,
    onDataChange,
    onRemoveItem,
    onSetCommentVisibility,
    className,
    showPriceReference,
    onRender,
    ...divProps
  } = props;

  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (onRender && rootRef.current) {
      window.setTimeout(() => {
        if (onRender && rootRef.current) {
          onRender(rootRef.current);
        }
      }, 16);
    }
  }, [onRender, index, data]);

  return (
    <div
      className={classNames("scha__bulk-preview__table__row", classes.previewTableRow, className)}
      {...divProps}
      ref={rootRef}
      data-index={index}
    >
      <div className="scha__bulk-preview__table__left-side">
        <div className={"px-3"}>{index + 1}</div>
      </div>
      <PreviewTableRow
        data={data}
        onDataChange={onDataChange}
        onRemoveItem={onRemoveItem}
        onSetCommentVisibility={onSetCommentVisibility}
        showPriceReference={showPriceReference}
      />
    </div>
  );
};

let PreviewTableRow: FC<Omit<Props, "index" | "onRender">> = ({
  data,
  onDataChange,
  onRemoveItem,
  onSetCommentVisibility,
  showPriceReference,
}) => {
  const { t } = useTranslation();
  const id = data.id;
  const [
    onQtyChange,
    onIdChange,
    onDateChange,
    onCustomOrderChange,
    updatePriceReference,
    onCommentChange,
    setCommentVisibility,
    removeItem,
  ] = useMemo(() => {
    return [
      (newValue: number) => onDataChange(id, { quantity: newValue }),
      (newProduct: ProductData, inputText: string) =>
        onDataChange(id, {
          designation: newProduct.designation,
          sapMaterialNumber: newProduct.sapMaterialNumber,
          inputText,
          marketType: newProduct.marketType,
        }),
      (newDate: Date) => onDataChange(id, { requestedDeliveryDate: newDate }),
      (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onDataChange(id, { customerOrderNumber: e.target.value }),
      (priceReference: string | undefined) => onDataChange(id, { priceReference }),
      (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onDataChange(id, { comment: e.target.value }),
      (showComment: boolean) => onSetCommentVisibility(id, showComment),
      () => onRemoveItem(id),
    ];
  }, [id, onDataChange, onRemoveItem, onSetCommentVisibility]);

  useEffect(() => {
    if (window.SCHA.config.hasEndCustomer) {
      updatePriceReference(window.SCHA.config.priceReferences[1].value);
    }
  }, []);

  const { isCommentVisible, messages } = data;

  const canMakeComments = canUseCommentsForProduct(data.marketType);

  const wasDateChanged = messages.some(
    (msg) =>
      msg.code === "bulkUpload.error.product.rdd.invalid" || msg.code === "bulkUpload.error.product.rdd.adjusted",
  );
  const isDateInvalid = messages.some((msg) => msg.code === "bulkUpload.error.product.rdd.tooFar");
  const onSale = data.onSale;

  const productError = messages.find((msg) => msg.code === "bulkUpload.error.product.id.invalid");
  const quantityError = messages.find((msg) => msg.code === "bulkUpload.error.product.quantity.invalid");
  const dateError = messages.find((msg) => {
    if (
      [
        "bulkUpload.error.product.rdd.invalid",
        "bulkUpload.error.product.rdd.tooFar",
        "bulkUpload.error.product.rdd.adjusted",
      ].includes(msg.code)
    ) {
      return msg;
    }
  });
  const priceReferenceError = messages.find((msg) => msg.code === "bulkUpload.error.product.reference.invalid");
  const productEntriesAdjustedInfo = messages.find((msg) => msg.code === "bulkUpload.error.product.info.adjusted");

  return (
    <div className="scha__bulk-preview__table__right-side" data-test-id={`bulk-upload-item`}>
      <Card disableHover={true} className={classes.previewRoundedCard}>
        <div
          className={classNames(
            "px-0 scha__bulk-preview__table__right-side__col-container",
            classes.previewRightSideColSection,
          )}
        >
          <div>
            <ProductAutoComplete
              key={data.productId}
              label={t("bulkUpload.table.header.productId")}
              value={data.productId}
              onChange={onIdChange}
              disableNewVerLink={true}
              variant={"outlined"}
              error={productError ? true : false}
              helperText={
                productError
                  ? t(productError.code)
                  : data.succeedingProductDesignation && (
                      <Trans i18nKey={"product.hasNewerVersion"}>
                        newer version is existing{" "}
                        <Link
                          style={{ color: "inherit", fontWeight: "600" }}
                          className={"d-inline"}
                          onClick={() =>
                            onDataChange(id, {
                              productId: data.succeedingProductDesignation,
                              inputText: data.succeedingProductDesignation,
                              previousProductId: data.productId,
                              succeedingProductDesignation: "",
                            })
                          }
                        >
                          {{ name: data.succeedingProductDesignation }}
                        </Link>
                      </Trans>
                    )
              }
              color={productError ? "error" : data.succeedingProductDesignation ? "info" : undefined}
            />
            {isLineLevelCustomerOrderNumberEnabled() && (
              <div className={"mt-2"}>
                <TextField
                  fullWidth
                  variant={"outlined"}
                  label={t("search.type.customerOrderNumber")}
                  value={data.customerOrderNumber}
                  onChange={onCustomOrderChange}
                />
              </div>
            )}
          </div>
          <div>
            {data.quantityVisible ? (
              <QuantityInput
                data-test-id={"bulk-upload-item-quantity"}
                label={t("cartPage.table.header.quantity.title")}
                onChange={onQtyChange}
                hideButtons={true}
                value={data.quantity}
                error={quantityError ? true : false}
                helperLabel={quantityError && t(quantityError.code)}
              />
            ) : (
              <Parameter label={t("input.quantity")} value={data.quantity} />
            )}
          </div>
          <div>
            <DeliveryDateInput
              label={t("cartPage.table.content.chooseDate")}
              onChange={onDateChange}
              data-test-id={"bulk-upload-item-date-selector-rdd"}
              date={data.requestedDeliveryDate ?? null}
              isHighlight={wasDateChanged} // info
              isInvalid={isDateInvalid} // error
              isDayDisabled={isDateInvalidForDelivery}
              variant={"outlined"}
              helperLabel={dateError && t(dateError.code)}
              iconClassName={classes.calendarIcon}
            />
          </div>

          {!onSale && showPriceReference && (
            <div>
              <PriceReferenceDropdown
                data-test-id={"bulk-upload-item-price-reference"}
                selected={data.priceReference}
                onChange={updatePriceReference}
                highlight={!!priceReferenceError}
                helperLabel={priceReferenceError && t(priceReferenceError.code)}
                size={"medium"}
              />
            </div>
          )}

          {onSale && <OnSaleIndicator />}
          <div className={classes.previewActionBtnSection}>
            {canMakeComments &&
              (isCommentVisible ? (
                <CommentsDisabledOutlinedIcon
                  data-test-id={"bulk-upload-item-btn-toggle-comment"}
                  className={"icon-clickable icon-clickable--primary"}
                  onClick={() => setCommentVisibility(!isCommentVisible)}
                  fontSize="medium"
                />
              ) : (
                <CommentOutlinedIcon
                  data-test-id={"bulk-upload-item-btn-toggle-comment"}
                  className={"icon-clickable icon-clickable--primary"}
                  onClick={() => setCommentVisibility(!isCommentVisible)}
                  fontSize="medium"
                />
              ))}
            <DeleteOutlineOutlinedIcon
              data-test-id={"bulk-upload-item-btn-remove"}
              className={classNames("icon-clickable icon-clickable--primary", canMakeComments ? "ml-3" : "ml-4")}
              onClick={removeItem}
              fontSize="medium"
            />
          </div>
        </div>
        {isCommentVisible && (
          <div className={"mt-3 px-3 scha__bulk-preview__table__comment-box"}>
            <TextField
              variant={"outlined"}
              multiline
              fullWidth
              data-test-id={"bulk-upload-item-input-comment"}
              inputProps={{ sx: { height: "90px !important" } }}
              helperText={t("bulkUpload.comment.limit")}
              onChange={onCommentChange}
              value={data.comment}
            />
          </div>
        )}
        <div className={"px-3 scha__bulk-preview__table__message-box"}>
          {productEntriesAdjustedInfo && <Alert severity="info">{t(productEntriesAdjustedInfo.code)}</Alert>}
          {data.marketType === ProductMarketType.PMA && <InfoProductMessagePMA />}
          {/* Hide this for now in SCHAEFF-33406 */}
          {/* {data.previousProductId && (
            <UndoButton
              onClick={() =>
                onDataChange(id, {
                  productId: data.previousProductId,
                  inputText: data.previousProductId,
                })
              }
            />
          )} */}
        </div>
      </Card>
    </div>
  );
};
PreviewTableRow = memo(PreviewTableRow);

export default PreviewTableRowWrapper;

const QuantityInput = styled(CartQuantityInput)({
  ".MuiFormHelperText-root": {
    fontSize: "12px",
  },
});

const ProductAutoComplete = styled(PreviewTableMaterialIdInput)(({ error, color }) => {
  const isHighlight = color === "info";
  const borderColor = isHighlight ? `${C_INFORMATION} !important` : error && `${C_ERROR} !important`;
  return {
    ".MuiInputLabel-shrink": {
      transform: "translate(14px, -9px) scale(0.75) !important",
    },
    ".MuiOutlinedInput-root": {
      padding: "8px !important",
      paddingLeft: "16px !important",
      ".MuiOutlinedInput-notchedOutline": {
        borderColor: borderColor,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: borderColor,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: borderColor,
      },
    },
    ".MuiFormLabel-colorInfo": {
      color: C_INFORMATION,
      "&.Mui-focused": {
        color: `${C_INFORMATION} !important`,
      },
    },
    ".MuiFormHelperText-root": {
      fontSize: "12px",
      color: isHighlight && `${C_INFORMATION} !important`,
      marginTop: "4px",
      marginRight: "16px",
      marginLeft: "16px",
    },
  };
});

const DeliveryDateInput = styled(DateInput)(({ isInvalid, isHighlight }) => {
  const borderColor = isHighlight ? `${C_INFORMATION} !important` : isInvalid && `${C_ERROR} !important`;
  return {
    ".MuiInputLabel-shrink": {
      transform: "translate(14px, -9px) scale(0.75) !important",
    },
    ".MuiOutlinedInput-root": {
      ".MuiOutlinedInput-notchedOutline": {
        borderColor: borderColor,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: borderColor,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: borderColor,
      },
    },
    ".MuiFormLabel-colorInfo": {
      color: C_INFORMATION,
      "&.Mui-focused": {
        color: `${C_INFORMATION} !important`,
      },
    },
    ".MuiFormHelperText-root": {
      fontSize: "12px",
      color: borderColor,
    },
  };
});

const OnSaleIndicator = () => {
  const { t } = useTranslation();
  return (
    <Stack direction="row" gap={1} alignItems="center" sx={{ height: "58px" }}>
      <SaleTag size={32} />
      <Typography color="salesCampaign" variant="bodyLarge">
        {t("feature.bulkEntry.tag.onSale")}
      </Typography>
    </Stack>
  );
};
